import DesktopSidebar from '@ui/DesktopSidebar'
import MobileBottomNav from '@ui/MobileBottomNav'
import MobileTopbar from '@ui/MobileTopbar'
import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom'

// Single breakpoint by design — every screen is verified at 375px first
// (design.md §0) and desktop is a widening, not a redesign.
const MOBILE_BREAKPOINT_PX = 880

const detectMobile = () =>
  typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX

const AdminShell = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobile, setIsMobile] = useState(detectMobile)

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobile())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const eventMatch = useMatch('/events/:id/*')
  const eventId = eventMatch?.params.id ?? null

  const topbarTitle = t(resolveTopbarTitleKey(location.pathname))

  const isList = !eventId && location.pathname === '/events'

  // The events list owns the create sheet; the FAB just navigates and the
  // page picks up ?create=1 — keeps sheet state local to the owning page.
  const onNewEvent = () => {
    navigate('/events?create=1')
  }

  return (
    <div className="min-h-dvh bg-cream-bg text-ink-900">
      {isMobile ? (
        <>
          {renderMobileTopbar({ isList, eventId, topbarTitle })}

          <main
            className="px-4 max-w-content mx-auto"
            style={{
              // Shell owns gutter + bottom safe-padding only — pages own their
              // top padding so a sticky filter bar can sit flush against the topbar.
              paddingBottom: 'calc(116px + env(safe-area-inset-bottom))',
            }}
          >
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </main>

          <MobileBottomNav
            eventId={eventId}
            onNewEvent={!eventId ? onNewEvent : undefined}
          />
        </>
      ) : (
        <div className="grid grid-cols-[var(--sidebar-w)_1fr]">
          <DesktopSidebar eventId={eventId} />
          <main className="px-8 pb-10 max-w-content mx-auto w-full">
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      )}
    </div>
  )
}

const renderMobileTopbar = ({
  isList,
  eventId,
  topbarTitle,
}: {
  isList: boolean
  eventId: string | null
  topbarTitle: string
}) => {
  if (isList) return <MobileTopbar variant="list" />
  if (eventId) return <MobileTopbar variant="event" title={topbarTitle} />
  return <MobileTopbar variant="simple" title={topbarTitle} />
}

const resolveTopbarTitleKey = (pathname: string): string => {
  // Scorecard is a leaf detail page — check before the broader players match.
  if (pathname.match(/^\/events\/[^/]+\/players\/[^/]+\/scorecard/))
    return 'page.event-scorecard'
  if (pathname.match(/^\/events\/[^/]+\/players/)) return 'page.event-players'
  if (pathname.match(/^\/events\/[^/]+\/teams/)) return 'page.event-teams'
  if (pathname.match(/^\/events\/[^/]+\/flights/)) return 'page.event-flights'
  if (pathname.match(/^\/events\/[^/]+\/poker/)) return 'page.event-poker'
  if (pathname.match(/^\/events\/[^/]+\/settings/)) return 'page.event-settings'
  if (pathname.startsWith('/events/')) return 'page.event-summary'
  if (pathname.startsWith('/users')) return 'page.users'
  if (pathname.match(/^\/account-settings\/profile/))
    return 'account-settings.sections.profile.title'
  if (pathname.match(/^\/account-settings\/payment/))
    return 'account-settings.sections.payment.title'
  if (pathname.match(/^\/account-settings\/communication/))
    return 'account-settings.sections.communication.title'
  if (pathname.match(/^\/account-settings\/legal/))
    return 'account-settings.sections.legal.title'
  if (pathname.startsWith('/account-settings')) return 'account-settings.title'
  if (pathname.startsWith('/platform/courses/')) return 'courses.detail.title'
  if (pathname.startsWith('/platform/courses')) return 'courses.title'
  if (pathname.startsWith('/platform/accounts/'))
    return 'platform-accounts.detail.title'
  if (pathname.startsWith('/platform/accounts'))
    return 'platform-accounts.title'
  if (pathname.startsWith('/platform/vipps-payments'))
    return 'platform-vipps.title'
  if (pathname.startsWith('/platform/sms')) return 'platform-sms.title'
  if (pathname.startsWith('/platform/reports')) return 'platform-reports.title'
  if (pathname.startsWith('/events')) return 'page.events'
  return 'brand.name'
}

export default AdminShell
